'use strict';

import loader from 'helpers/loader.js';
import PaginatedList from 'commons/views/PaginatedList.js';
import ModalForm from 'commons/views/ModalForm';
import modal from 'helpers/modal';
import template from '../../templates/timesheet/List.ejs';
import addOvertimeTemplate from '../../templates/timesheet/OvertimeModal.ejs';
import RowView from './Row.js';
import moment from 'moment';
import Alerts from 'lib/alerts';

const AddOvertimeModal = ModalForm.extend({
    events() {
        var parentEvents = _.isFunction(ModalForm.prototype.events) ?
            ModalForm.prototype.events() : ModalForm.prototype.events;

        return _.extend(_.clone(parentEvents), {
            'change .js-ext-employees': 'selectEmployees',
            'change .js-ext-overtime': 'selectOvertime'
        });
    },

    initialize(options) {
        this.options = _.defaults(options || {}, {
            template: addOvertimeTemplate,
            employees: null,
            overtimeItems: null,
            actOnSave: false,
            actOnCancel: false,
            actOnDestroy: false,
            templateExtra: {}
        });

        this.options.templateExtra = _.extend(this.options.templateExtra, {
            employees: this.options.employees || [],
            overtimeItems: this.options.overtimeItems || []
        });

        this.selectedEmployees = [];
        this.selectedOvertime = this.options.overtimeItems.map(o => o.id);
        ModalForm.prototype.initialize.apply(this, [this.options]);
        this.bindHandlers();
        return this.render();
    },

    bindHandlers() {
        this.options.employees.on('sync', this.render, this);
        this.options.employees.on('sync', this.updateSelectedEmployees, this);
        return this;
    },

    unbindHandlers() {
        this.options.employees.off(null, null, this);
        return this;
    },

    updateSelectedEmployees() {
        this.$('.js-ext-employees').val(false);
        this.selectedEmployees = this.options.employees.map(e => e.id);
    },

    render() {
        this.$el.html(this.options.template(
            this.options.templateExtra || {}
        ));
        this.$('.js-chosen').chosen({width: '100%'});
        return this;
    },

    selectEmployees(ev) {
        const $select = this.$(ev.currentTarget);
        const selected = $select.val();

        if (selected.length === 0) {
            this.selectedEmployees = this.options.employees.map(e => e.id);
            return;
        }
        this.selectedEmployees = selected;
    },

    selectOvertime(ev) {
        const $select = this.$(ev.currentTarget);
        const selected = $select.val();

        if (selected.length === 0) {
            this.selectedOvertime = this.options.overtimeItems.map(o => o.id);
            return;
        }
        this.selectedOvertime = selected;
    },

    save(ev) {
        ev.preventDefault();
        this.trigger('save', {
            employees: this.selectedEmployees,
            overtime: this.selectedOvertime
        });

        this.updateSelectedEmployees();
        this.selectedOvertime = this.options.overtimeItems.map(o => o.id);
    }
});

module.exports = PaginatedList.extend({
    tagName: 'table',
    className: 'table table-striped table-timesheet',

    events() {
        return Object.assign(PaginatedList.prototype.events(), {
            'click .js-save': 'save',
            'click .js-cancel': 'fetchTimeEntries',
            'click .js-add-overtime': 'showAddOvertime'
        });
    },

    initialize(options) {
        this.options = _.defaults(options || {}, {
            unsubmittedPayroll: null,
            date: moment().startOf('week').toDate(),
            overtimeItems: []
        });
        this.addOvertimeModal = new AddOvertimeModal({
            employees: this.collection,
            overtimeItems: this.options.overtimeItems
        });
        this.views = [];
        this.collection.on('sync', this.fetchTimeEntries, this);
        this.collection.fetchPage(undefined, {validate: false});
        this.stopLoader = loader.startFullPage(this.$('table'));
        this.bindHandlers();
        return this;
    },

    bindHandlers() {
        this.addOvertimeModal.on('save', this.addOvertimeTimeEntries, this);
        this.addOvertimeModal.on('save', this.hideAddOvertime, this);

        app.events.on('navigate', function(status) {
            if (this.collection.dirtyTimeEntries()) {
                status.unsaved = true;
                status.msg     = 'You have unsaved changes.  Exiting this ' +
                    'page will result in the changes being discarded.';
            } else {
                status.unsaved = false;
            }
        }, this);

        return this;
    },

    unbindHandlers() {
        this.addOvertimeModal.off(null, null, this);
        app.events.off(null, null, this);
        return this;
    },

    beforePageChange() {
        if (this.collection.dirtyTimeEntries()) {
            return Alerts.show('confirm', {
                title: 'Unsaved changes',
                text: 'There are unsaved changes. Navigating to a different' +
                    'page will result in the changes being discarded.',
                confirmButtonText: 'Continue',
                cancelButtonText: 'Stay'
            });
        }
        return Promise.resolve(true);
    },

    render() {
        this.$el.html(template({
            dayLabels: moment.weekdaysShort(),
            total: this.collection.length,
            pagination: this.collection.getPaginationInfo()
        }));
        this.renderModels();
        this.hideLoader();

        this.undelegateEvents();
        this.delegateEvents();
        return this;
    },

    fetchTimeEntries() {
        this.stopLoader = loader.startFullPage(this.$('table'));
        this.collection.fetchTimeEntries(app.company, this.options.date)
            .then(() => this.render());
    },

    renderModels() {
        this.views.forEach(view => view.remove());
        this.views = [];

        this.collection.forEach(model => {
            const view = new RowView({
                model: model,
                date: this.options.date
            });
            view.on('re-render', this.render, this);
            this.views.push(view.render());
            this.$('.js-rows-anchor').after(view.$el);
        });
        return this;
    },

    hideLoader() {
        this.stopLoader();
    },

    resetPayrollConfirm() {
        return new Promise((resolve, reject) => {
            if (!_.isObject(this.options.unsubmittedPayroll)) {
                return resolve(true);
            }

            modal.confirm({
                title: 'Your saved payroll will be modified!',
                text:  'You have an unsubmitted payroll saved. ' +
                    'If you continue, the payroll details of all employees ' +
                    'will be updated based on the modifications you are ' +
                    'about to save. Also, any unsaved payroll details ' +
                    'will be lost.',
                customClass: 'sweet-alert-error',
                confirmButtonText: 'Yes, Save Timesheet',
                callback: confirm => {
                    if (confirm) {
                        resolve(true);
                    } else {
                        reject();
                    }
                }
            });
        });
    },

    save(ev) {
        ev.preventDefault();

        return this.resetPayrollConfirm()
            .then(() => {
                this.stopLoader =
                    loader.startFullScreen(this.$(ev.currentTarget));
            })
            .then(() => {
                return this.collection.saveTimeEntries();
            })
            // reset the payroll
            .then(() => {
                if (this.options.unsubmittedPayroll) {
                    return this.options.unsubmittedPayroll.reset();
                }

                return Promise.resolve(true);
            })
            .then(() => {
                modal.showAlertSuccess('Your changes have been saved');
            })
            .catch(() => {
                this.stopLoader =
                    loader.startFullScreen(this.$(ev.currentTarget));
            })
            .finally(() => this.hideLoader());
    },

    navigate() {
        app.mainRouter.navigate('timesheet');
    },

    showAddOvertime() {
        this.addOvertimeModal.show();
    },

    hideAddOvertime() {
        this.addOvertimeModal.hide();
    },

    addOvertimeTimeEntries(data) {
        const selectedEmployees = data.employees.map(id => {
            return this.collection.get(id);
        });
        const selectedOvertime = data.overtime.map(id => {
            return this.options.overtimeItems.get(id);
        });
        selectedEmployees.forEach(employee => {
            selectedOvertime.forEach(overtime => {
                let date = this.options.date;
                const commencementDate = employee.get('commencementDate') ?
                    moment(employee.get('commencementDate')).startOf('day') :
                    moment(date);
                if (moment(date).startOf('day').isBefore(commencementDate)) {
                    date = commencementDate.toDate();
                }
                employee.getTimeEntry(date, overtime);
            });
        });

        this.render();
    },

    remove() {
        PaginatedList.prototype.remove.apply(this, arguments);
        this.views.forEach(view => view.remove());
        this.views = [];
        this.undelegateEvents();
        this.unbindHandlers();
    }
});



// WEBPACK FOOTER //
// ./app/employer/views/timesheet/List.js